"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PostHogTelemetry = void 0;
exports.createTelemetry = createTelemetry;
const consent_1 = require("./consent");
const identity_1 = require("./identity");
const props_1 = require("./props");
const noop_1 = require("./noop");
/** EU capture host — chosen for privacy optics (data stays in the EU). */
const DEFAULT_HOST = 'https://eu.i.posthog.com';
/** Auto-flush once this many events are buffered. */
const MAX_BATCH = 20;
/** Idle auto-flush so a long-lived host (extension/MCP server) doesn't sit on events. */
const FLUSH_INTERVAL_MS = 15000;
/** Hard cap on a capture request — telemetry must never hang a workflow. */
const REQUEST_TIMEOUT_MS = 3000;
/**
 * PostHog capture client. Every event carries the anonymous `distinct_id`
 * (install id), a per-process `$session_id`, and the content-free common props.
 * All I/O is fire-and-forget with a bounded timeout and swallowed errors — a
 * telemetry failure can never throw into, block, or slow a Loom action.
 *
 * Construct via `createTelemetry`, never directly, so the consent gate is always
 * applied.
 */
class PostHogTelemetry {
    constructor(config) {
        this.queue = [];
        this.host = (config.host ?? DEFAULT_HOST).replace(/\/+$/, '');
        this.apiKey = config.apiKey;
        this.distinctId = (0, identity_1.getOrCreateInstallId)(config.configDir);
        this.sessionId = (0, identity_1.newSessionId)();
        this.common = (0, props_1.buildCommonProps)({
            surface: config.surface,
            loomVersion: config.loomVersion,
            env: config.env,
        });
    }
    track(event, props = {}) {
        try {
            this.queue.push({
                event,
                properties: {
                    ...this.common,
                    ...props,
                    distinct_id: this.distinctId,
                    $session_id: this.sessionId,
                },
                timestamp: new Date().toISOString(),
            });
            if (this.queue.length >= MAX_BATCH) {
                void this.flush();
            }
            else {
                this.scheduleFlush();
            }
        }
        catch {
            /* telemetry must never disturb the caller */
        }
    }
    async flush() {
        if (this.timer) {
            clearTimeout(this.timer);
            this.timer = undefined;
        }
        if (this.queue.length === 0)
            return;
        const batch = this.queue;
        this.queue = [];
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
        try {
            await fetch(`${this.host}/batch/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ api_key: this.apiKey, batch }),
                signal: controller.signal,
            });
        }
        catch {
            /* silent — a dropped batch is acceptable; never surface it */
        }
        finally {
            clearTimeout(timeout);
        }
    }
    scheduleFlush() {
        if (this.timer)
            return;
        this.timer = setTimeout(() => void this.flush(), FLUSH_INTERVAL_MS);
        // Never keep the process alive solely to flush telemetry.
        this.timer.unref?.();
    }
}
exports.PostHogTelemetry = PostHogTelemetry;
/**
 * The only telemetry constructor callers should use. Returns the shared Noop
 * client unless consent passes (opt-in enabled AND a key present), so a disabled
 * or misconfigured setup is structurally incapable of emitting.
 */
function createTelemetry(config) {
    if (!(0, consent_1.resolveConsent)(config) || !config.apiKey) {
        return noop_1.noopTelemetry;
    }
    return new PostHogTelemetry({ ...config, apiKey: config.apiKey });
}
